import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { RadioGroup } from "@headlessui/react";
import { Button } from "@/components/Button";
import { htmlToPlainText, isHtml } from "./utils";
import axios from "axios";
import { BaseLivlyApiResponse } from "@/types/Base";
import { BASE_API_URL } from "@/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Spinner } from "@/components/Spinner";
import { InsuranceQuoteContext } from "@/components/InsuranceQuoteLayout";

export interface Outcome {
  outcomeName: string;
  text: string;
}

export interface QuestionOption {
  questionOptionId: number;
  option: string;
  optionValue: string;
  questionIdNext: number | null;
  outcomeId: number | null;
  nextQuestion: Question | null;
  validation: any | null;
  outcome: Outcome | null;
}

interface Question {
  questionId: number;
  questionSetId: number | null;
  questionName: string;
  question: string;
  helpText: string | null;
  instructions: string | null;
  dataType: string;
  maxSize: number;
  isShown: boolean;
  options: QuestionOption[];
}

export interface Questionnaire {
  questionnaireId: number;
  questionnaireName: string;
  questions: Question[];
}

interface LiabilityQuestionnaireAnswer {
  QuestionId: number;
  QuestionOptionId: number;
  TextAnswer: string;
}

export interface LiabilityQuestionnaireSubmission {
  QuestionnaireId: number;
  Answers: LiabilityQuestionnaireAnswer[];
}

interface QuestionnaireResponse {
  isApproved: boolean;
  outcomeName: string;
  pmsValues: [
    {
      fieldName: string;
      value: string;
    }
  ];
}

const LibalityQuestion300K = ({
  initialUrl,
  outcomeUrl,
  isInsuranceOnly,
  isFetching
}: {
  initialUrl: string;
  outcomeUrl: string;
  isInsuranceOnly: boolean;
  isFetching: boolean;
}) => {
  const { selectionMetaData, updateMetaData } =
    useOutletContext<InsuranceQuoteContext>();
  const { data, isLoading } = useGetLiabilityQuestions300K(initialUrl);
  const update300KAssurantQuote = usePostLiabilityQuestions300K(outcomeUrl);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  // Get the questions array from the 300K data
  const questions: Question[] = useMemo(() => data?.questions ?? [], [data]);

  const visibleQuestions = getVisibleQuestions(questions, answers);

  // Remove answers for questions that are no longer visible
  useEffect(() => {
    const visibleNames = new Set(visibleQuestions.map((q) => q.questionName));
    setAnswers((prev) => {
      const filtered: Record<string, string> = {};
      for (const key in prev) {
        if (visibleNames.has(key)) filtered[key] = prev[key];
      }
      return filtered;
    });
    // Only run when visibleQuestions changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(visibleQuestions.map((q) => q.questionName))]);

  if (isLoading || isFetching) {
    return (
      <div className="flex items-center justify-center flex-1 my-56">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  const handleAnswerChange = (questionName: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionName]: value }));
  };

  const renderOptions = (question: any) => (
    <RadioGroup
      value={answers[question.questionName] || ""}
      onChange={(value) => handleAnswerChange(question.questionName, value)}
    >
      <div className="space-y-2">
        {question.options.map((opt: any) => (
          <RadioGroup.Option
            key={opt.optionValue}
            value={opt.optionValue}
            className="flex items-center space-x-2 cursor-pointer px-4"
          >
            {({ checked }) => (
              <>
                <span
                  className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                    checked ? "border-blue-600" : "border-gray-400"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full bg-blue-600 transition ${
                      checked ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </span>
                <span>{opt.option}</span>
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );

  const renderQuestionText = (question: any) => {
    const q = question.question;
    if (isHtml(q)) {
      return <span>{htmlToPlainText(q)}</span>;
    }
    return <span>{q}</span>;
  };

  // Submit handler for Next button
  const handleSubmit = async () => {
    const payload = {
      QuestionnaireId: data?.questionnaireId || 0,
      Answers: Object.entries(answers).map(([questionName, answer]) => {
        // Find the question by questionName
        const question = visibleQuestions.find(
          (q) => q.questionName === questionName
        );
        // Find the selected option by answer (optionValue)
        const option = question?.options.find(
          (opt) => opt.optionValue === answer
        );
        return {
          QuestionId: question?.questionId ?? 0,
          QuestionOptionId: option?.questionOptionId ?? 0,
          TextAnswer: answer
        };
      })
    };
    await update300KAssurantQuote.mutateAsync(payload, {
      onSuccess: (data) => {
        if (data.isApproved) {
          updateMetaData({
            ...selectionMetaData!,
            uwAnswers: data?.pmsValues[0]?.value ?? ""
          });
          if (isInsuranceOnly) {
            navigate(`../insurance-quote/summary`, {
              state: { isInsuranceOnly: true }
            });
          } else {
            navigate(`../insurance-quote/summary`);
          }
        } else {
          navigate(`../insurance-quote/not-qualified`);
        }
      },
      onError: () => {
        navigate(`../insurance-quote/not-qualified`);
      }
    });
  };

  const isNextButtonDisabled = visibleQuestions.some(
    (question) => !answers[question.questionName]
  );
  return (
    <div>
      <h2 className="font-medium">Liability Questions</h2>
      <p className="text-gray-500 mb-4">
        Property loss underwriting questions are required by your state based on
        your selection. Please answer the following questions.
      </p>
      {visibleQuestions.map((question: any, idx: number) => {
        return (
          <div className="mb-6" key={question.questionName}>
            <p className="font-medium text-gray-700 mb-2">
              {renderQuestionText(question)}
            </p>
            {renderOptions(question)}
          </div>
        );
      })}
      {/* Show finish if last question or outcome */}
      {visibleQuestions.length > 0 &&
      (visibleQuestions[visibleQuestions.length - 1].options.find(
        (opt: any) =>
          answers[
            (visibleQuestions[visibleQuestions.length - 1] as any).questionName
          ] === opt.optionValue && opt.outcome
      ) ||
        visibleQuestions.length === questions.length) ? (
        <Button
          disabled={isNextButtonDisabled || update300KAssurantQuote.isLoading}
          onClick={handleSubmit}
        >
          {update300KAssurantQuote.isLoading && <Spinner />}
          {update300KAssurantQuote.isLoading ? "Please wait..." : "Next"}
        </Button>
      ) : null}
    </div>
  );
};

// Helper to flatten questions and their nextQuestion chains
function getVisibleQuestions(
  questions: Question[],
  answers: Record<string, string>
) {
  const visible: Question[] = [];
  let i = 0;
  let currentQuestions = questions;
  while (i < currentQuestions.length) {
    const q = currentQuestions[i];

    // Custom logic for AreAnyDogsServiceAnimals
    if (
      q.questionName === "AreAnyDogsServiceAnimals" &&
      q.instructions &&
      q.instructions.includes("If No, proceed ") &&
      answers["AreDogsExcludedBreeds"] !== "Y"
    ) {
      // Skip this question and move to next
      i++;
      continue;
    }

    visible.push(q);
    const selectedOption = q.options.find(
      (opt) => answers[q.questionName] === opt.optionValue
    );
    // If selected option has a nextQuestion, show it (and its chain)
    if (selectedOption && selectedOption.nextQuestion) {
      currentQuestions = [selectedOption.nextQuestion];
      i = 0;
      continue;
    }
    // If selected option has outcome, stop
    if (selectedOption && selectedOption.outcome) break;
    // If not answered, stop
    if (!answers[q.questionName]) break;
    i++;
  }
  return visible;
}

export function useGetLiabilityQuestions300K(url: string) {
  const encodedUrl = encodeURIComponent(url);
  const getLiabilityQuestions = async () => {
    const result = await axios.get<BaseLivlyApiResponse<Questionnaire[]>>(
      `${BASE_API_URL}/v2/RentersInsurance/GH/Questionnaires/Search?questionnaireUrl=${encodedUrl}`
    );
    //Always it returns an array with one item
    return result.data.Data[0];
  };

  return useQuery(["liability-questions-300K", url], getLiabilityQuestions);
}

export const usePostLiabilityQuestions300K = (url: string) => {
  const encodedUrl = encodeURIComponent(url);
  const postTicket = async (payload: LiabilityQuestionnaireSubmission) => {
    const result = await axios.post<
      BaseLivlyApiResponse<QuestionnaireResponse>
    >(
      `${BASE_API_URL}/v2/RentersInsurance/GH/Questionnaires/Resolution?outcomeUrl=${encodedUrl}`,
      payload
    );
    return result.data.Data;
  };

  return useMutation(postTicket);
};

export default LibalityQuestion300K;
